/**
 * Driver Scan Page - Updated to use new /api/scan/* endpoints
 * Supports: Pickup, Drop at PUDO, and Final Delivery
 */
import { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Html5Qrcode } from 'html5-qrcode';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import api from '../lib/api';
import {
  ArrowLeft,
  QrCode,
  Camera,
  CheckCircle,
  AlertCircle,
  Package,
  Truck,
  MapPin,
  Scan,
  KeyRound,
  Users,
  Flashlight,
  FlashlightOff
} from 'lucide-react';

const COLORS = {
  green: '#00A86B',
  yellow: '#FFD300',
  black: '#000000',
};

// Short beep + vibration on a successful scan so the driver knows it worked hands-free.
let _scanAudioCtx = null;
const scanFeedback = () => {
  try { if (navigator.vibrate) navigator.vibrate([40, 30, 40]); } catch (e) { /* noop */ }
  try {
    _scanAudioCtx = _scanAudioCtx || new (window.AudioContext || window.webkitAudioContext)();
    const ctx = _scanAudioCtx;
    if (ctx.state === 'suspended') ctx.resume();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = 'sine';
    o.frequency.setValueAtTime(880, ctx.currentTime);
    o.frequency.setValueAtTime(1180, ctx.currentTime + 0.09);
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.22);
    o.connect(g);
    g.connect(ctx.destination);
    o.start();
    o.stop(ctx.currentTime + 0.24);
  } catch (e) { /* audio not available */ }
};

const SCAN_TYPES = {
  pickup: {
    id: 'pickup',
    label: 'Pickup from Vendor',
    description: 'Scan Package QR when collecting from merchant',
    icon: Package,
    color: COLORS.green,
    bgColor: 'bg-green-600',
    endpoint: '/scan/package',
    scanTypeParam: 'pickup',
    qrType: 'package'
  },
  dropoff_pudo: {
    id: 'dropoff_pudo',
    label: 'Drop at PUDO',
    description: 'Scan Package QR when leaving at PUDO point',
    icon: MapPin,
    color: COLORS.yellow,
    bgColor: 'bg-amber-500',
    endpoint: '/scan/package',
    scanTypeParam: 'dropoff_pudo',
    qrType: 'package'
  },
  pickup_pudo: {
    id: 'pickup_pudo',
    label: 'Pickup from PUDO',
    description: 'Scan Package QR when collecting from PUDO',
    icon: Truck,
    color: COLORS.black,
    bgColor: 'bg-gray-800',
    endpoint: '/scan/package',
    scanTypeParam: 'pickup_pudo',
    qrType: 'package'
  },
  deliver: {
    id: 'deliver',
    label: 'Deliver to Customer',
    description: 'Scan RECIPIENT QR or enter OTP to complete delivery',
    icon: Users,
    color: '#7C3AED',
    bgColor: 'bg-purple-600',
    endpoint: '/scan/recipient',
    qrType: 'recipient'
  }
};

// Turn backend/technical scan errors into clear, driver-friendly messages.
const friendlyScanError = (raw, tab) => {
  const m = String(raw || '').toLowerCase();
  if (m.includes('not found') || m.includes('no package') || m.includes('invalid qr'))
    return "We couldn't find this package. Check the code or QR and try again.";
  if (m.includes('cannot perform pickup') || (m.includes('pickup') && m.includes('status')))
    return 'This package isn\'t ready for pickup yet (it may not be accepted or was already collected).';
  if (m.includes('already') && (m.includes('picked') || m.includes('delivered')))
    return 'This package was already scanned at this stage.';
  if (m.includes('cannot perform') && m.includes('deliver'))
    return 'This package isn\'t ready to be delivered yet — pick it up first.';
  if (m.includes('otp') || m.includes('pin') || m.includes('recipient'))
    return tab === 'deliver' ? 'Wrong delivery OTP/PIN or recipient QR. Ask the customer and try again.' : String(raw);
  if (m.includes('permission') || m.includes('not allowed') || m.includes('role'))
    return "You don't have permission for this scan on this package.";
  if (m.includes('network') || m.includes('timeout') || m.includes('failed to fetch'))
    return 'Network problem — check your connection and try again.';
  return raw || 'Scan failed. Please try again.';
};

const DriverScanNew = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const _initTab = (searchParams.get('tab') === 'deliver' || searchParams.get('type') === 'dropoff') ? 'deliver' : 'pickup';
  const [activeTab, setActiveTab] = useState(_initTab);
  const [scanning, setScanning] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [manualCode, setManualCode] = useState(_initTab !== 'deliver' ? (searchParams.get('code') || '') : '');
  const [packageId, setPackageId] = useState(searchParams.get('code') || '');
  const [otp, setOtp] = useState('');
  const [result, setResult] = useState(null);
  const [cameraError, setCameraError] = useState(null);
  const html5QrRef = useRef(null);
  const READER_ID = 'driver-qr-reader';
  const [torchOn, setTorchOn] = useState(false);
  const [torchSupported, setTorchSupported] = useState(false);

  const currentScanType = SCAN_TYPES[activeTab];

  useEffect(() => {
    return () => {
      // stop the camera/decoder on unmount
      if (html5QrRef.current) {
        try { html5QrRef.current.stop().then(() => html5QrRef.current && html5QrRef.current.clear()).catch(() => {}); } catch (e) { /* noop */ }
        html5QrRef.current = null;
      }
    };
  }, []);

  const _beginDecode = async (facingMode) => {
    const qr = new Html5Qrcode(READER_ID, { verbose: false });
    html5QrRef.current = qr;
    await qr.start(
      { facingMode },
      { fps: 10, qrbox: { width: 230, height: 230 }, aspectRatio: 1.0 },
      (decodedText) => { onDecoded(decodedText); },
      () => { /* ignore per-frame decode misses */ }
    );
    // Detect flashlight/torch support on the active camera track
    setTorchOn(false);
    try {
      const caps = qr.getRunningTrackCapabilities && qr.getRunningTrackCapabilities();
      setTorchSupported(!!(caps && caps.torch));
    } catch (e) {
      setTorchSupported(false);
    }
  };

  const toggleTorch = async () => {
    if (!html5QrRef.current) return;
    const next = !torchOn;
    try {
      await html5QrRef.current.applyVideoConstraints({ advanced: [{ torch: next }] });
      setTorchOn(next);
    } catch (e) {
      setTorchSupported(false);
      toast.error('Flashlight isn\'t available on this device');
    }
  };

  const startCamera = async () => {
    setCameraError(null);
    setResult(null);
    setScanning(true);
    // Let the reader container mount before html5-qrcode attaches its <video>.
    await new Promise((r) => setTimeout(r, 60));
    try {
      await _beginDecode('environment');
      toast.info(`Point the camera at the ${currentScanType.qrType === 'recipient' ? 'Recipient' : 'Package'} QR`);
    } catch (err) {
      // Rear camera unavailable (e.g. laptop) — fall back to the front camera.
      try {
        if (html5QrRef.current) { try { await html5QrRef.current.clear(); } catch (e) {} html5QrRef.current = null; }
        await _beginDecode('user');
        toast.info('Using front camera — point it at the QR');
      } catch (err2) {
        html5QrRef.current = null;
        setScanning(false);
        const denied = (err2 && (err2.name === 'NotAllowedError' || /permission/i.test(String(err2))));
        setCameraError(denied
          ? 'Camera permission was blocked. Allow camera access in your browser settings, or use manual entry below.'
          : 'Could not open the camera on this device. Use manual entry below.');
        toast.error('Could not open camera');
      }
    }
  };

  const stopCamera = async () => {
    if (html5QrRef.current) {
      try { await html5QrRef.current.stop(); } catch (e) { /* already stopped */ }
      try { await html5QrRef.current.clear(); } catch (e) { /* noop */ }
      html5QrRef.current = null;
    }
    setTorchOn(false);
    setTorchSupported(false);
    setScanning(false);
  };

  const onDecoded = async (decodedText) => {
    if (processing) return;
    scanFeedback();
    await stopCamera();
    toast.success('QR detected');
    handleScan(decodedText);
  };

  const handleScan = async (qrData) => {
    if (processing) return;
    
    setProcessing(true);
    stopCamera();
    
    try {
      let response;
      
      if (activeTab === 'deliver') {
        // Delivery requires /api/scan/recipient
        if (!packageId.trim()) {
          throw new Error('Please enter Package ID first');
        }
        
        const idVal = packageId.trim();
        const payload = { gps_location: null };
        if (/^PKG/i.test(idVal)) {
          payload.package_id = idVal;
        } else {
          payload.tracking_code = idVal;
        }
        
        // Check if we have OTP or QR token
        if (otp.trim()) {
          payload.otp = otp.trim();
        } else if (qrData) {
          payload.recipient_qr_token = qrData;
        } else {
          throw new Error('Please enter OTP or scan Recipient QR');
        }
        
        response = await api.post('/scan/recipient', payload);
      } else {
        // Package scans use /api/scan/package?scan_type=xxx
        response = await api.post(`/scan/package?scan_type=${currentScanType.scanTypeParam}`, {
          qr_token: qrData || manualCode.trim(),
          gps_location: null,
          notes: null
        });
      }
      
      setResult({
        success: true,
        message: response.data.message || (activeTab === 'deliver' ? 'Delivery completed!' : 'Package picked up!'),
        nextStep: activeTab === 'deliver'
          ? 'All done — this delivery is complete.'
          : ((activeTab === 'pickup' || activeTab === 'pickup_pudo') ? 'Advancing to the delivery step…' : 'Scan recorded. Continue the delivery.'),
        data: response.data
      });

      scanFeedback();
      toast.success(response.data.message || 'Scan verified!');

      // Auto-advance to the DELIVER step after a successful pickup.
      if (activeTab === 'pickup' || activeTab === 'pickup_pudo') {
        const pkgRef = response.data?.tracking_id || response.data?.tracking_code
          || response.data?.package_id || (qrData || manualCode || '').trim();
        setTimeout(() => {
          setActiveTab('deliver');
          if (pkgRef) setPackageId(pkgRef);
          setOtp('');
          setManualCode('');
          setResult(null);
          toast.info('Ready to deliver — scan the recipient QR or enter their OTP');
        }, 2000);
      }
    } catch (error) {
      const raw = error.response?.data?.detail || error.message || 'Scan failed';
      setResult({
        success: false,
        message: friendlyScanError(raw, activeTab)
      });
      toast.error(friendlyScanError(raw, activeTab));
    } finally {
      setProcessing(false);
      setManualCode('');
    }
  };

  const handleManualSubmit = (e) => {
    e.preventDefault();
    if (activeTab === 'deliver') {
      handleScan(manualCode.trim());
    } else if (manualCode.trim()) {
      handleScan(manualCode.trim());
    }
  };

  const resetScan = () => {
    setResult(null);
    setManualCode('');
    setPackageId('');
    setOtp('');
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="driver-scan-page">
      {/* Header */}
      <header className="bg-[#00A86B] text-white px-4 py-4">
        <div className="flex items-center gap-3 max-w-4xl mx-auto">
          <Button
            variant="ghost"
            onClick={() => navigate('/driver')}
            className="p-2 hover:bg-green-600 rounded-full text-white"
            data-testid="back-button"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex items-center gap-2">
            <QrCode className="h-6 w-6" />
            <h1 className="text-lg font-bold">Driver Scanner</h1>
          </div>
        </div>
      </header>

      <main className="max-w-md mx-auto p-4 space-y-4">
        {/* Scan Type Tabs */}
        <div className="flex gap-1 bg-gray-100 p-1 rounded-xl" data-testid="scan-tabs">
          {Object.values(SCAN_TYPES).map((type) => (
            <button
              key={type.id}
              onClick={() => { setActiveTab(type.id); resetScan(); }}
              className={`flex-1 py-2 px-2 rounded-lg text-xs font-medium transition-all ${
                activeTab === type.id 
                  ? `${type.bgColor} text-white` 
                  : 'text-gray-600 hover:bg-gray-200'
              }`}
              data-testid={`tab-${type.id}`}
            >
              <type.icon className="h-4 w-4 mx-auto mb-1" />
              {type.label.split(' ')[0]}
            </button>
          ))}
        </div>

        {/* Current Mode Info */}
        <div className={`${currentScanType.bgColor} text-white rounded-xl p-4`}>
          <div className="flex items-center gap-3">
            <currentScanType.icon className="h-8 w-8" />
            <div>
              <h2 className="font-bold">{currentScanType.label}</h2>
              <p className="text-sm opacity-90">{currentScanType.description}</p>
            </div>
          </div>
        </div>

        {/* Result Display */}
        {result && (
          <div 
            className={`rounded-xl p-6 text-center ${
              result.success 
                ? 'bg-green-50 border border-green-200' 
                : 'bg-red-50 border border-red-200'
            }`}
            data-testid="scan-result"
          >
            {result.success ? (
              <CheckCircle className="h-14 w-14 text-green-500 mx-auto mb-3" />
            ) : (
              <AlertCircle className="h-14 w-14 text-red-500 mx-auto mb-3" />
            )}
            <h3 className={`text-lg font-bold mb-2 ${result.success ? 'text-green-800' : 'text-red-800'}`}>
              {result.success ? 'Success!' : 'Failed'}
            </h3>
            <p className={`text-sm ${result.success ? 'text-green-700' : 'text-red-700'}`}>
              {result.message}
            </p>
            {result.success && result.nextStep && (
              <p className="mt-2 text-sm font-medium text-green-800" data-testid="scan-next-step">
                {result.nextStep}
              </p>
            )}
            {result.success && result.data && (
              <div className="mt-3 bg-white rounded-lg p-3 text-left text-sm">
                {result.data.new_status && (
                  <p><span className="text-gray-500">New Status:</span> <span className="font-semibold">{result.data.new_status}</span></p>
                )}
                {result.data.tracking_code && (
                  <p><span className="text-gray-500">Tracking:</span> <span className="font-mono">{result.data.tracking_code}</span></p>
                )}
              </div>
            )}
            <div className="mt-4 flex gap-2 justify-center">
              <Button onClick={resetScan} className={currentScanType.bgColor}>
                Scan Another
              </Button>
              <Button variant="outline" onClick={() => navigate('/driver')} data-testid="return-home-btn">
                Return to Home
              </Button>
            </div>
          </div>
        )}

        {/* Scanning UI */}
        {!result && (
          <>
            {/* For Delivery - need package ID first */}
            {activeTab === 'deliver' && (
              <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Package ID or Tracking Code *
                </label>
                <Input
                  type="text"
                  value={packageId}
                  onChange={(e) => setPackageId(e.target.value)}
                  placeholder="Enter Package ID"
                  className="w-full"
                  data-testid="package-id-input"
                />
              </div>
            )}

            {/* Camera Scanner */}
            {scanning ? (
              <div className="relative rounded-xl overflow-hidden bg-black">
                {/* html5-qrcode injects its own <video> into this element */}
                <div id={READER_ID} className="w-full [&_video]:w-full [&_video]:object-cover" />
                <Button
                  onClick={stopCamera}
                  className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-red-500 hover:bg-red-600 z-10"
                  data-testid="stop-camera-btn"
                >
                  Stop Camera
                </Button>
                <p className="absolute top-4 left-1/2 -translate-x-1/2 text-white text-sm bg-black/60 px-3 py-1 rounded-full z-10">
                  {activeTab === 'deliver' ? 'Scan RECIPIENT QR' : 'Scan Package QR'}
                </p>
                {torchSupported && (
                  <button
                    type="button"
                    onClick={toggleTorch}
                    data-testid="torch-toggle-btn"
                    aria-label="Toggle flashlight"
                    className={`absolute top-3 right-3 z-10 h-11 w-11 rounded-full flex items-center justify-center shadow-lg transition-colors ${torchOn ? 'bg-yellow-400 text-black' : 'bg-black/60 text-white'}`}
                  >
                    {torchOn ? <Flashlight className="h-5 w-5" /> : <FlashlightOff className="h-5 w-5" />}
                  </button>
                )}
              </div>
            ) : (
              <Button
                onClick={startCamera}
                disabled={processing || (activeTab === 'deliver' && !packageId.trim())}
                className={`w-full py-8 ${currentScanType.bgColor} hover:opacity-90 text-white rounded-xl flex flex-col items-center gap-2`}
                data-testid="start-camera-btn"
              >
                <Camera className="h-10 w-10" />
                <span className="font-semibold">
                  {activeTab === 'deliver' ? 'Scan Recipient QR' : 'Scan Package QR'}
                </span>
              </Button>
            )}

            {cameraError && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-center text-sm text-amber-700">
                {cameraError}
              </div>
            )}

            <div className="flex items-center gap-4">
              <div className="flex-1 border-t border-gray-300" />
              <span className="text-gray-500 text-sm">OR</span>
              <div className="flex-1 border-t border-gray-300" />
            </div>

            {/* Manual Entry */}
            <form onSubmit={handleManualSubmit} className="space-y-3" data-testid="manual-form">
              {activeTab === 'deliver' ? (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Enter OTP (from customer)
                    </label>
                    <Input
                      type="text"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value)}
                      placeholder="6-digit OTP"
                      maxLength={6}
                      className="w-full text-center text-xl tracking-widest font-mono"
                      data-testid="otp-input"
                    />
                  </div>
                  <p className="text-xs text-gray-500 text-center">
                    Or enter recipient QR token below
                  </p>
                  <Input
                    type="text"
                    value={manualCode}
                    onChange={(e) => setManualCode(e.target.value)}
                    placeholder="Recipient QR token"
                    className="w-full"
                    data-testid="manual-qr-input"
                  />
                </>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Enter Package QR Token
                  </label>
                  <Input
                    type="text"
                    value={manualCode}
                    onChange={(e) => setManualCode(e.target.value)}
                    placeholder="Package QR token or tracking code"
                    className="w-full"
                    data-testid="manual-input"
                  />
                </div>
              )}
              
              <Button
                type="submit"
                disabled={processing || (activeTab === 'deliver' ? (!packageId.trim() || (!otp.trim() && !manualCode.trim())) : !manualCode.trim())}
                className="w-full bg-gray-800 hover:bg-gray-900 text-white"
                data-testid="submit-btn"
              >
                {processing ? (
                  <span className="flex items-center gap-2">
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                    Processing...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Scan className="h-4 w-4" />
                    {activeTab === 'deliver' ? 'Complete Delivery' : 'Submit Scan'}
                  </span>
                )}
              </Button>
            </form>

            {/* Warning for delivery */}
            {activeTab === 'deliver' && (
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-center text-sm text-purple-700">
                <strong>Final Delivery:</strong> Only Recipient QR or OTP works here.
                Package QR cannot complete delivery.
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default DriverScanNew;
